import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";
import { useLang } from "@/i18n";
import { L } from "@/components/L";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-[var(--ink)] mb-2">{label}</label>
    {children}
    {error && <p id={`${id}-error`} className="mt-1.5 text-sm text-[#b42318]" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const inputCls = (err) => `w-full rounded-2xl bg-[var(--cream)] px-4 py-3.5 text-base outline-none transition-[box-shadow] duration-300 ${err ? "shadow-[inset_0_0_0_1.5px_#b42318]" : "shadow-[inset_0_0_0_1.5px_var(--line)]"} focus:shadow-[inset_0_0_0_2px_var(--green-deep)]`;

export const ContactForm = () => {
  const { t, lang } = useLang();
  const f = t.contact.form;
  const [params] = useSearchParams();
  const [v, setV] = useState({ name: "", email: "", subject: params.get("subject") || "", message: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const validate = () => {
    const e = {};
    if (v.name.trim().length < 2) e.name = f.required;
    if (!v.email.trim()) e.email = f.required;
    else if (!EMAIL_RE.test(v.email.trim())) e.email = f.invalidEmail;
    if (v.subject.trim().length < 2) e.subject = f.required;
    if (!v.message.trim()) e.message = f.required;
    else if (v.message.trim().length < 10) e.message = f.minMessage;
    if (!v.consent) e.consent = f.consentRequired;
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/api/contact`, { ...v, lang });
      setStatus("success");
      setV({ name: "", email: "", subject: "", message: "", consent: false, website: "" });
    } catch {
      setStatus("error");
    }
  };

  const set = (k) => (e) => setV((s) => ({ ...s, [k]: k === "consent" ? e.target.checked : e.target.value }));
  const aria = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined });

  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label={f.name} error={errors.name}><input id="name" autoComplete="name" value={v.name} onChange={set("name")} className={inputCls(errors.name)} data-testid="contact-name-input" {...aria("name")} /></Field>
        <Field id="email" label={f.email} error={errors.email}><input id="email" type="email" autoComplete="email" value={v.email} onChange={set("email")} className={inputCls(errors.email)} data-testid="contact-email-input" {...aria("email")} /></Field>
      </div>
      <Field id="subject" label={f.subject} error={errors.subject}><input id="subject" value={v.subject} onChange={set("subject")} className={inputCls(errors.subject)} data-testid="contact-subject-input" {...aria("subject")} /></Field>
      <Field id="message" label={f.message} error={errors.message}><textarea id="message" rows={6} value={v.message} onChange={set("message")} className={`${inputCls(errors.message)} resize-y`} data-testid="contact-message-input" {...aria("message")} /></Field>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <div>
        <label className="flex gap-3 items-start cursor-pointer">
          <input id="consent" type="checkbox" checked={v.consent} onChange={set("consent")} className="mt-1 h-5 w-5 accent-[var(--green-deep)] shrink-0" data-testid="contact-consent-checkbox" {...aria("consent")} />
          <span className="text-sm text-[var(--ink-soft)] leading-relaxed">{f.consent} <L to="privacy" className="underline text-[var(--green-deep)] font-semibold" data-testid="contact-privacy-link">{f.privacyLink}</L></span>
        </label>
        {errors.consent && <p id="consent-error" className="mt-1.5 text-sm text-[#b42318]" data-testid="consent-error">{errors.consent}</p>}
      </div>
      <p className="text-xs text-[var(--ink-soft)]">{f.requiredNote}</p>
      {status === "success" && <p role="status" className="flex gap-3 items-start rounded-2xl bg-[var(--green-pale)] p-4 text-[var(--green-night)]" data-testid="contact-success"><CheckCircle2 className="shrink-0" size={20} /> {f.success}</p>}
      {status === "error" && <p role="alert" className="flex gap-3 items-start rounded-2xl bg-[var(--coral-pale)] p-4 text-[#7a1d14]" data-testid="contact-error"><AlertCircle className="shrink-0" size={20} /> {f.error}</p>}
      <button type="submit" disabled={status === "sending"} className="btn btn-primary w-full sm:w-auto disabled:opacity-70" data-testid="contact-submit-btn">
        {status === "sending" ? <><Loader2 size={16} className="animate-spin" /> {f.sending}</> : <><Send size={16} /> {f.submit}</>}
      </button>
    </form>
  );
};
